import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Trash2, Tag, ShoppingBag, X, AlertTriangle } from "lucide-react";
import { EmptyState, VegDot } from "@/components/common";
import { useCart } from "@/context/CartContext";
import { inr, apiError } from "@/lib/api";

export default function Cart() {
  const nav = useNavigate();
  const { cart, count, updateLine, removeLine, setMeta } = useCart();
  const [code, setCode] = useState("");

  if (!count || !cart) {
    return (
      <div className="min-h-screen bg-surface max-w-[520px] mx-auto">
        <Header nav={nav} />
        <EmptyState icon={ShoppingBag} title="Your cart is empty" subtitle="Add delicious food from restaurants near you." cta="Browse restaurants" onCta={() => nav("/")} testid="empty-cart" />
      </div>
    );
  }

  const applyCoupon = async () => {
    try { await setMeta({ coupon: code }); toast.success("Coupon applied!"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeCoupon = async () => { await setMeta({ clear_coupon: true }); setCode(""); };
  const setTip = async (t) => { try { await setMeta({ tip: cart.tip === t ? 0 : t }); } catch (e) { toast.error(apiError(e)); } };

  const proceed = () => {
    const bad = cart.items.some((i) => !i.available);
    if (bad || !cart.restaurant_open) return toast.error("Please review unavailable items before checkout.");
    nav("/checkout");
  };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-40">
      <Header nav={nav} />
      <div className="px-4 py-3">
        {cart.warnings?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2" data-testid="cart-warnings">
            <AlertTriangle className="text-amber-600 mt-0.5" size={16} />
            <div className="text-xs text-amber-800">{cart.warnings.join(". ")}. Please review before checkout.</div>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-border p-4">
          <div className="flex items-center gap-3 pb-3 border-b border-border">
            <img src={cart.restaurant.image} alt="" className="w-11 h-11 rounded-xl object-cover" />
            <div><div className="font-bold text-ink">{cart.restaurant.name}</div><div className="text-xs text-ink-600">{cart.restaurant.delivery_time_min} min</div></div>
          </div>
          {cart.items.map((it) => (
            <div key={it.line_id} className="flex items-start gap-2 py-3 border-b border-border last:border-0" data-testid={`cart-item-${it.menu_item_id}`}>
              <VegDot veg={it.veg} className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-ink">{it.name}</div>
                {it.variant && <div className="text-[11px] text-ink-600">{it.variant}</div>}
                {it.addons?.length > 0 && <div className="text-[11px] text-ink-600">+ {it.addons.map((a) => a.name).join(", ")}</div>}
                {it.spice && <div className="text-[11px] text-ink-600">Spice: {it.spice}</div>}
                {!it.available && <div className="text-[11px] text-red-500 font-semibold">Currently unavailable</div>}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center border border-border rounded-lg bg-white">
                  <button onClick={() => (it.qty === 1 ? removeLine(it.line_id) : updateLine(it.line_id, -1))} className="w-7 h-7 flex items-center justify-center text-coral" data-testid={`cart-minus-${it.menu_item_id}`}>{it.qty === 1 ? <Trash2 size={14} /> : <Minus size={14} />}</button>
                  <span className="w-6 text-center text-sm font-bold" data-testid={`cart-qty-${it.menu_item_id}`}>{it.qty}</span>
                  <button onClick={() => updateLine(it.line_id, 1)} className="w-7 h-7 flex items-center justify-center text-coral" data-testid={`cart-plus-${it.menu_item_id}`}><Plus size={14} /></button>
                </div>
                <span className="text-sm font-semibold">{inr(it.line_total)}</span>
              </div>
            </div>
          ))}
          <button onClick={() => nav(`/restaurant/${cart.restaurant.id}`)} className="mt-3 text-sm text-coral font-semibold flex items-center gap-1"><Plus size={15} /> Add more items</button>
        </div>

        <div className="bg-white rounded-2xl border border-border p-4 mt-3">
          {cart.coupon ? (
            <div className="flex items-center justify-between bg-emerald-50 rounded-xl px-3 py-2.5">
              <span className="text-sm font-bold text-veg flex items-center gap-1.5"><Tag size={15} /> {cart.coupon} applied · saved {inr(cart.discount)}</span>
              <button onClick={removeCoupon} data-testid="remove-coupon"><X size={16} className="text-ink-600" /></button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code (e.g. BITE50)" className="flex-1 min-w-0 bg-surface rounded-xl px-3 py-2.5 text-sm outline-none uppercase" data-testid="coupon-input" />
              <button onClick={applyCoupon} className="bg-ink text-white font-bold px-4 rounded-xl text-sm tap" data-testid="apply-coupon">Apply</button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-border p-4 mt-3">
          <div className="font-bold text-sm text-ink mb-2">Add a tip for your rider</div>
          <div className="flex gap-2">
            {[20, 30, 50].map((t) => <button key={t} onClick={() => setTip(t)} className={`px-4 py-2 rounded-xl text-sm font-bold border ${cart.tip === t ? "bg-coral text-white border-coral" : "bg-white border-border"}`} data-testid={`tip-${t}`}>{inr(t)}</button>)}
          </div>
        </div>

        <BillDetails cart={cart} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-[520px] mx-auto bg-white border-t border-border p-4">
        <button onClick={proceed} className="w-full bg-coral hover:bg-coral-hover text-white font-bold py-3.5 rounded-xl flex items-center justify-between px-5 tap" data-testid="proceed-checkout">
          <span>{inr(cart.total)}</span><span>Proceed to Checkout →</span>
        </button>
      </div>
    </div>
  );
}

export function BillDetails({ cart, title = "Bill details", totalLabel = "Total Paid" }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 mt-3" data-testid="bill-details">
      <h3 className="font-bold text-ink mb-2">{title}</h3>
      <Row l="Food Cost" v={inr(cart.food_subtotal)} />
      {cart.discount > 0 && <Row l="Discount" v={`- ${inr(cart.discount)}`} green />}
      <Row l="Delivery Fee" v={cart.delivery_fee === 0 ? "FREE" : inr(cart.delivery_fee)} />
      {cart.tip > 0 && <Row l="Rider tip" v={inr(cart.tip)} />}
      <Row l="GST" v={cart.gst_label || "No GST — Enjoy bro"} muted testid="bill-gst" />
      <Row l="Tax" v={cart.tax_label || "Enjoy bro, chill"} muted testid="bill-tax" />
      <div className="flex justify-between border-t border-border mt-2 pt-2 font-bold text-ink"><span>{totalLabel}</span><span data-testid="cart-total">{inr(cart.total)}</span></div>
    </div>
  );
}

function Header({ nav }) {
  return (
    <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
      <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
      <h1 className="font-display font-bold text-lg">Your Cart</h1>
    </div>
  );
}
function Row({ l, v, green, muted, testid }) {
  return <div className="flex justify-between text-sm py-0.5" data-testid={testid}><span className="text-ink-600">{l}</span><span className={green ? "text-veg font-semibold" : muted ? "text-emerald-600 font-semibold" : "text-ink"}>{v}</span></div>;
}
